"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "src/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { Alert, Box, CircularProgress, Snackbar } from "@mui/material";
import { Header } from "@components/header";
import * as Sentry from "@sentry/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useProductWithMediaDefinition } from "src/hooks/api/useProductWithMedia";
import { useOrder } from "src/contexts";
import { useCartDrawer } from "src/contexts/cart-drawer-context";
import { useCheckoutRegistration } from "src/hooks/useCheckoutRegistration";
import {
  orderApi,
  photosApi,
  productWithMediaVariantApi,
} from "src/services/api";
import { orderQueryKey } from "src/hooks/useOrder";
import type { CanvasDesign } from "src/components/canvas/canvas-design";
import type {
  CanvasVariant,
  VariantAxis,
} from "src/components/canvas/variant-resolver";
import {
  clearCanvasDraft,
  readCanvasDraft,
  writeCanvasDraft,
} from "src/components/canvas/canvas-draft-store";
import { getCurrencyBasedOnDomain } from "src/utils/domain.util";
import { finishForMaterialParam, initialCanvasVariantId } from "src/components/canvas/initial-variant";

// react-konva must not run on the server — ssr:false is required, exactly as
// the greeting-card design route does.
import CanvasUploadPanel from "src/components/canvas/CanvasUploadPanel";
import CanvasCoupon from "src/components/canvas/CanvasCoupon";
import { apiMessage } from "src/components/map/map-errors";
import {
  printableFile,
  UnprintableHeicError,
} from "src/components/canvas/printable-file";

const CanvasDesigner = dynamic(
  () => import("src/components/canvas/CanvasDesigner"),
  {
    ssr: false,
    loading: () => (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    ),
  },
);

/** The generated client types these responses as `object`. */
interface RawDefinition {
  id: string;
  kind?: string;
  name?: string | Record<string, string>;
  variantAxes?: VariantAxis[];
  minQuantity?: number;
}

/**
 * The canvas editor, given the product to open. Rendered by both
 * /[locale]/canvas/[definitionId] and the id-less public entry route
 * /[locale]/canvas — see resolve-canvas-definition.ts for why the latter
 * exists.
 */
export default function CanvasEditor({
  definitionId,
}: {
  definitionId: string;
}) {
  const t = useTranslations("editor");
  const locale = useLocale();
  // The market's currency, not a literal "eur" — this is the same render-body
  // call the product store makes (product-store/single-product.tsx:106). The
  // children only format with it; the amount for it is selected per variant
  // from priceCurrencies in variant-price.ts.
  const currency = getCurrencyBasedOnDomain();
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const queryClient = useQueryClient();

  const { data: rawDefinition, isLoading, error } =
    useProductWithMediaDefinition(definitionId);
  const definition = rawDefinition as RawDefinition | undefined;

  const { ensureOrderExists } = useOrder();
  const { openCartDrawer } = useCartDrawer();

  const [variants, setVariants] = useState<CanvasVariant[] | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [checkoutTarget, setCheckoutTarget] = useState<
    { orderId: string; guestToken: string } | null
  >(null);

  const draftKey = definitionId ? `canvas-draft:v1:${definitionId}` : null;
  const [restored] = useState(() =>
    draftKey ? readCanvasDraft(draftKey, Date.now()) : null,
  );
  // A bundle slot links here with ?variant=: that size opens preselected
  // (see initialCanvasVariantId).
  const searchParams = useSearchParams();
  const urlVariantId = searchParams?.get("variant") ?? null;
  // /produkti and the website link here with ?material=canvas|paper so the
  // "Na platnu" door opens on stretched canvas and "Velike fotografije" on paper.
  const preferredFinish = finishForMaterialParam(searchParams?.get("material"));

  // ── Variants ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!definitionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res =
          await productWithMediaVariantApi.getManyBaseProductWithMediaVariantControllerProductWithMediaVariant(
            undefined, // fields
            undefined, // s
            // @dataui/crud filter syntax. Filtering on a relation column needs
            // the relation joined, hence the join argument below.
            //
            // `active` matters as much as the definition: a retired variant is
            // one the shop cannot make. Without this the editor kept offering
            // "Napeto na okvir" after the framed canvases were switched off,
            // and the customer only found out when checkout refused the size.
            [`definition.id||$eq||${definitionId}`, "active||$eq||true"],
            undefined, // or
            ["sortOrder,ASC"],
            ["definition"],
            200,
            0,
          );
        if (cancelled) return;
        const data = res.data as unknown as
          | CanvasVariant[]
          | { data?: CanvasVariant[] };
        setVariants(Array.isArray(data) ? data : (data?.data ?? []));
      } catch (err) {
        if (!cancelled) {
          Sentry.captureException(err);
          setPageError(t("loadProductFailed"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [definitionId, t]);

  // ── Restore a draft's photo ────────────────────────────────────────────────
  useEffect(() => {
    if (!restored?.sourceKey || sourceKey) return;
    void resolveAndSetImage(restored.sourceKey);
    // resolveAndSetImage is stable for our purposes; restoring runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restored]);

  const resolveAndSetImage = useCallback(async (key: string) => {
    const res = await photosApi.photoControllerGetDownloadUrl(key);
    const url = (res.data as unknown as { url?: string })?.url ?? String(res.data);
    const size = await new Promise<{ width: number; height: number }>(
      (resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () =>
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = url;
      },
    );
    setSourceKey(key);
    setImageUrl(url);
    setImageSize(size);
  }, []);

  const handleFile = useCallback(
    async (picked: File) => {
      setPageError(null);
      setUploading(true);
      try {
        // A HEIC becomes a JPEG before anything is uploaded: the print render
        // cannot decode HEIC, and finding that out after payment is too late.
        // See printable-file.ts.
        const file = await printableFile(picked);

        // The presigned-URL endpoint is behind JwtOrGuestAuthGuard, so a guest
        // session has to exist BEFORE the upload — ensureOrderExists is what
        // sets the x-guest-token header on the shared axios instance. Uploading
        // first returns 401.
        await ensureOrderExists();

        const presigned = await photosApi.photoControllerGetPresignedUrls({
          files: [{ name: file.name, type: file.type }],
        });
        const entry = (presigned.data as unknown as {
          urls?: { url: string; key: string }[];
        })?.urls?.[0];
        if (!entry?.url || !entry?.key) {
          throw new Error("Presigned URL is missing");
        }
        const put = await fetch(entry.url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);
        await resolveAndSetImage(entry.key);
      } catch (err) {
        Sentry.captureException(err);
        setPageError(
          err instanceof UnprintableHeicError
            ? t("canvasHeicUnsupported")
            : err instanceof Error
              ? err.message
              : t("addToCartFailed"),
        );
      } finally {
        setUploading(false);
      }
    },
    [ensureOrderExists, resolveAndSetImage, t],
  );

  // Cart drawer checkout registration. Shared with the art and photography
  // pages — see the hook for why forgetting it is invisible until nobody can
  // buy anything.
  useCheckoutRegistration(checkoutTarget);

  const handleDraftChange = useCallback(
    (state: { design: CanvasDesign; variantId: string | null }) => {
      if (!draftKey) return;
      writeCanvasDraft(
        draftKey,
        { design: state.design, axisValues: {}, sourceKey, variantId: state.variantId },
        Date.now(),
      );
    },
    [draftKey, sourceKey],
  );

  const handleAddToCart = useCallback(
    async ({
      design,
      variant,
      quantity,
    }: {
      design: CanvasDesign;
      variant: CanvasVariant;
      quantity: number;
    }) => {
      if (!sourceKey || !definition) return;
      setPageError(null);
      setAddingToCart(true);
      try {
        const { orderId, guestToken } = await ensureOrderExists();

        // No renderKey/previewKey: unlike greeting cards, the backend renders
        // the preview on create and the print file after payment.
        await orderApi.orderControllerAddProductWithMedia(
          orderId,
          {
            definitionId: definition.id,
            variantId: variant.id,
            quantity,
            media: [
              {
                slotKey: "front",
                rank: 0,
                designData: design as unknown as object,
                sourcePhotoKeys: [sourceKey],
              },
            ],
          } as never,
          guestToken,
        );

        if (draftKey) clearCanvasDraft(draftKey);

        try {
          const updated = await orderApi.getOneBaseOrderControllerOrder(
            orderId,
            guestToken,
            undefined,
            [
              "orderCalendars",
              "orderPhotos",
              "orderProductDetails",
              "productWithMediaOrders",
              "productWithMediaOrders.definition",
            ],
            undefined,
          );
          if (updated.data?.id) {
            queryClient.setQueryData(orderQueryKey(updated.data.id), updated.data);
          }
        } catch (refreshErr) {
          // Non-fatal: the cart refreshes on next open.
          console.error("CanvasPage: order refresh failed", refreshErr);
        }

        setCheckoutTarget({ orderId, guestToken });
        openCartDrawer();
      } catch (err: unknown) {
        Sentry.captureException(err);
        // Surface the backend's actual reason — Axios's "Request failed with
        // status code 400" hides variant/design refusals. class-validator's
        // English is not a reason a customer can read: apiMessage drops it
        // and the translated fallback shows instead (ops walk B8).
        setPageError(apiMessage(err) || t("addToCartFailed"));
      } finally {
        setAddingToCart(false);
      }
    },
    [
      definition,
      draftKey,
      ensureOrderExists,
      openCartDrawer,
      queryClient,
      sourceKey,
      t,
    ],
  );

  if (isLoading || variants === null) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !definition) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ p: 4 }}>
          <Alert severity="error">{t("productNotFound")}</Alert>
        </Box>
      </>
    );
  }

  const definitionName =
    typeof definition.name === "string"
      ? definition.name
      : ((definition.name as Record<string, string>)?.[locale] ??
         (definition.name as Record<string, string>)?.sl ??
         "");

  return (
    <>
      <Header sticky={false} />
      <Snackbar
        open={!!pageError}
        onClose={() => setPageError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setPageError(null)}>
          {pageError}
        </Alert>
      </Snackbar>

      {!imageUrl || !imageSize ? (
        <CanvasUploadPanel
          productName={definitionName}
          variants={variants}
          currency={currency}
          locale={locale}
          uploading={uploading}
          galleryHref={`/${locale}/art`}
          preferredFinish={preferredFinish}
          onFile={(file) => void handleFile(file)}
          t={t as unknown as (key: string) => string}
        />
      ) : (
        <CanvasDesigner
          variants={variants}
          productName={definitionName}
          imageUrl={imageUrl}
          imageSize={imageSize}
          currency={currency}
          locale={locale}
          axes={definition.variantAxes ?? []}
          initialDesign={restored?.design}
          preferredFinish={preferredFinish}
          initialVariantId={initialCanvasVariantId(
            urlVariantId,
            variants,
            restored?.variantId,
          )}
          onChangePhoto={() => {
            // Back to the upload panel. Clearing the image is what swaps the
            // two views; the size and design choices are deliberately kept, so
            // swapping the photo does not throw away the rest of the order.
            setSourceKey(null);
            setImageUrl(null);
            setImageSize(null);
          }}
          addingToCart={addingToCart}
          onChange={handleDraftChange}
          onAddToCart={handleAddToCart}
          renderCoupon={(pending) => <CanvasCoupon pending={pending} />}
          t={t as unknown as (key: string) => string}
        />
      )}
    </>
  );
}
